import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { type IGetUserPermissionsResponse } from '../logic/api';
import { SessionContext } from '../utility/useSession';
import { ReleaseFlag, useReleaseManagement } from '@/utility/feature-management';
import {
  getPermissionParent,
  permissionsToFlagMap,
  type Permission,
} from '../components/users/rolesAndPermissions/types';
import { useRbacFlags, getFlagState } from './useRbacFlags';
import { useLocalPermissionOverride } from '../devtools/local-permissions/localPermissionOverrideContext';

interface usePermissionsReturn {
  userPermissions: string[];
  baseUserPermissions: string[] | undefined;
  hasPermission: (permission: Permission) => boolean;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

const hasSamePermissions = (permissions: string[], base: string[] | undefined) => {
  if (!base) {
    return false;
  }
  const baseSet = new Set(base);
  const permissionSet = new Set(permissions);
  return permissionSet.size === baseSet.size && [...permissionSet].every((permission) => baseSet.has(permission));
};

export const useUserPermissions = (): usePermissionsReturn => {
  const { current: session } = useContext(SessionContext);

  const isRbacReleased = useReleaseManagement(ReleaseFlag.releaseRbac);
  const rbacFlags = useRbacFlags();

  const userUuid = session?.site?.user?.uuid;
  const localOverride = useLocalPermissionOverride();

  const {
    data: userPermissionsData,
    isLoading,
    error,
    refetch
  } = useQuery<IGetUserPermissionsResponse | null, Error>({
    queryKey: ['userPermissions', userUuid],
    queryFn: async () => {
      if (!userUuid || !session?.context) {
        throw new Error('No user UUID or session context available');
      }

      const response = await fetch(`${session.context.url}/rbac/getUserPermissions/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...session.context.headers,
        },
        body: JSON.stringify({ userUUID: userUuid }),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch user permissions: ${response.statusText}`);
      }
      return response.json();
    },
    enabled: !!userUuid && isRbacReleased && !!session?.context,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes,
  });

  const baseUserPermissions = userPermissionsData?.permissions;

  const overridePermissions = localOverride && !hasSamePermissions(localOverride, baseUserPermissions)
    ? localOverride
    : undefined;
  const userPermissions = overridePermissions ?? baseUserPermissions ?? [];
  const hasRoles = (session.site?.user.roles?.length ?? 0) > 0;

  const isPermissionEnforced = (permission: Permission): boolean => {
    const featureFlag = permissionsToFlagMap.get(permission);
    return isRbacReleased && hasRoles && getFlagState(rbacFlags, featureFlag);
  };

  const hasPermission = (permission: Permission): boolean => {
    if (overridePermissions !== undefined) {
      if (!overridePermissions.includes(permission)) return false;
      const parentPermission = getPermissionParent(permission);
      return !parentPermission || overridePermissions.includes(parentPermission);
    }

    if (!isPermissionEnforced(permission)) {
      return true;
    }

    if (!userPermissions.includes(permission)) {
      return false;
    }

    const parentPermission = getPermissionParent(permission);
    return !parentPermission ||
      !isPermissionEnforced(parentPermission) ||
      userPermissions.includes(parentPermission);
  };

  return {
    userPermissions,
    baseUserPermissions,
    hasPermission,
    isLoading,
    error: error as Error | null,
    refetch
  };
};
